import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import { translate } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import DragDrop from 'components/common/Draggable/DragDrop';
import { LOGIN_STEP } from 'state/login/types';
import ClearPinBtn from './ClearPinBtn';
import Header from './Header';
import common from '../style/common.module.scss';
import style from '../style/StudentPin.module.scss';
import commonAssets from 'utils/commonAssets';

const { images } = commonAssets;
const t = (key) => translate('common', key);

const numbers = [
  { id: '1', card: images.letterCard1 },
  { id: '2', card: images.letterCard2 },
  { id: '3', card: images.letterCard3 },
  { id: '4', card: images.letterCard4 },
  { id: '5', card: images.letterCard5 }
];

const letters = [
  { id: 'A', card: images.letterCardA },
  { id: 'B', card: images.letterCardB },
  { id: 'C', card: images.letterCardC },
  { id: 'D', card: images.letterCardD },
  { id: 'E', card: images.letterCardE }
];

class StudentPinTiles extends React.Component {
  constructor(props) {
    super(props);

    const { users, selectedUserId } = props;
    const { login, username } = users.find((user) => user.id === selectedUserId);

    this.state = {
      pin: '',
      activePieces: [],
      username,
      login,
      whiteBoxFocus: false,
      errorId: undefined
    };

    this.onDragSuccess = this.onDragSuccess.bind(this);
    this.pinRefComponent = new Array(() => createRef());
  }

  componentDidUpdate(prevProps) {
    const prevError = prevProps.error || {};
    const currentError = this.props.error || {};
    const currentSuccess = this.props.success;

    if (!currentSuccess && prevError.id !== currentError.id) {
      this.pinRefComponent = new Array(() => createRef());
      this.setState({
        pin: '',
        activePieces: [],
        whiteBoxFocus: false,
        errorId: currentError.id
      });
    }
  }

  componentDidMount() {
    this.announceStudentPinEvent(
      'This form will auto submit and redirect you to the application on successfully entering your Secret Code.'
    );
  }

  announceStudentPinEvent(message) {
    const el = document.getElementById('assistiveStudentPinAnnouncements');

    if (!el) return;

    el.innerText = '';

    setTimeout(() => {
      el.innerText = message;
    }, 500);
  }

  checkPinMatch() {
    const { pin, login } = this.state;
    const { selectedClassId, selectedUserId, loginUser } = this.props;

    if (pin.length === login.length) {
      setTimeout(() => loginUser(selectedUserId, pin, selectedClassId), 600);
    }
  }

  onDragSuccess({ id }) {
    const pinChar = id.slice(0, -1);

    this.setSecretBoxAriaLabel(
      `secretBoxImg${this.state.activePieces.length + 1}`,
      `secret code box ${this.state.activePieces.length + 1} contains ${pinChar}`
    );

    if (!this.state.activePieces.includes(id)) {
      const pinEventContext = isFinite(pinChar) ? 'Number' : 'Letter';
      this.announceStudentPinEvent(
        `${pinEventContext} ${pinChar} card added to the secret code input.`
      );

      this.setState(
        ({ pin, activePieces }) => ({
          pin: `${pin}${pinChar}`,
          activePieces: [...activePieces, id],
          whiteBoxFocus: true
        }),
        this.checkPinMatch
      );
    }
  }

  onPinReset = (x, y, id) => {
    if (this.state.activePieces.includes(id)) {
      this.setState(({ pin }) => ({
        activePieces: [...this.state.activePieces.filter((item) => item !== id)],
        pin: pin.slice(0, -1)
      }));
    }
  };

  onPinCleanUp = () => {
    const { clearLoginError } = this.props;

    const items = this.pinRefComponent.filter(
      (item) =>
        item.key >= 0 && (item.instance.state.deltaX !== 0 || item.instance.state.deltaY !== 0)
    );
    items.forEach((item, index) => {
      item.instance.state.deltaX = 0;
      item.instance.state.deltaY = 0;

      this.setSecretBoxAriaLabel(`secretBoxImg${index + 1}`, `secret code box ${index + 1}`);
    });

    this.setState({ pin: '', activePieces: [], whiteBoxFocus: false });

    this.announceStudentPinEvent('User secret code has been cleared');
    clearLoginError();
  };

  onPasswordKeyUp = (e, id) => {
    switch (e.key) {
      case 'Tab': {
        this.setState({ whiteBoxFocus: true });
        break;
      }
      case 'Enter': {
        const item = this.pinRefComponent.filter((item) => item.id == id && !item.used)[0];
        if (item != null) {
          item.instance.handleTap();
          item.used = true;
        }
        break;
      }
    }
  };

  createPinReference = (instance, id, key) => {
    const pinRef = this.pinRefComponent;
    if (instance != null && pinRef.filter((item) => item.id == id).length < 6) {
      pinRef.push({ id, key, instance, used: false });
    }
  };

  focusOnDestinationBox = (index) => {
    const classList = document.body.classList;
    const { pin, whiteBoxFocus } = this.state;
    return whiteBoxFocus && index == pin.length && classList.contains('using-keyboard')
      ? style.boxFocus
      : style.box;
  };

  onFocusOut = () => {
    this.setState({ whiteBoxFocus: false });
  };

  onPinCardClick = () => {
    this.setState({ whiteBoxFocus: false });
  };

  setSecretBoxAriaLabel = (id, label) => {
    const droppableSecretBoxImhEl = document.getElementById(id);
    droppableSecretBoxImhEl?.setAttribute('aria-label', label);
  };

  render() {
    const { pin, username, login, activePieces, errorId } = this.state;
    const { goToStep, error, contentScaling, success, loading } = this.props;

    const dragTargets = new Array(login.length).fill(0).map((_, index) => ({
      correct: true,
      content: (
        <div>
          <div className={this.focusOnDestinationBox(index)} id={`secret${index}`}>
            <img
              alt={t('SecretCodeBox')}
              aria-label={`secret code box ${index + 1}`}
              className={[style.img, style[`img${contentScaling}`]].join(' ')}
              id={`secretBoxImg${index + 1}`}
              role="img"
              src={letters[0].card.src}
            />
          </div>
        </div>
      ),
      ref: React.createRef()
    }));

    const boxes = dragTargets.map((dragTarget, id) => (
      <DragDrop.Target forwardedRef={dragTarget.ref} key={id}>
        {dragTarget.content}
      </DragDrop.Target>
    ));

    const upperCaseFirstLetter = (text) => text.charAt(0).toUpperCase() + text.slice(1);

    const repeat = new Array(6).fill(0);
    const pinCard = (divId, id, card, index) => (
      <button
        aria-label={`${upperCaseFirstLetter(divId)} ${id} ${t('card')}`}
        className={['login-button', style.dragGroup, style[`dragGroup${contentScaling}`]].join(' ')}
        id={`${divId}-${index + 1}`}
        onKeyUp={(e) => this.onPasswordKeyUp(e, id, card)}
        onBlur={(e) => this.onFocusOut(e)}
        onMouseDown={() => this.onPinCardClick()}
      >
        {repeat.map((_r, key) => (
          <DragDrop.Draggable
            ref={(instance) => {
              this.createPinReference(instance, id, key);
            }}
            allowTap={activePieces.length < login.length && !activePieces.includes(`${id}${key}`)}
            allowDrag={
              activePieces.length < login.length &&
              !(
                activePieces.includes(`${id}${key}`) &&
                activePieces[activePieces.length - 1] !== `${id}${key}`
              )
            }
            id={`${id}${key}`}
            key={key}
            classList={{
              dragged: style.dragged,
              initial: style.draggable
            }}
            targets={
              activePieces.includes(`${id}${key}`)
                ? dragTargets.slice(pin.length - 1, pin.length)
                : dragTargets.slice(pin.length, pin.length + 1)
            }
            callback={this.onDragSuccess}
            onTap={this.onDragSuccess}
            onBadDrag={this.onPinReset}
            soundFx={{ sndCorrect: true, sndWrong: true }}
          >
            <img
              className={[style.card, style[`card${contentScaling}`]].join(' ')}
              src={card.src}
              alt=""
            />
          </DragDrop.Draggable>
        ))}
      </button>
    );

    const lettersList = letters.map(({ id, card }, index) => (
      <li key={id}>{pinCard('letter', id, card, index)}</li>
    ));
    const numbersList = numbers.map(({ id, card }, index) => (
      <li key={id}>{pinCard('number', id, card, index)}</li>
    ));

    return (
      <div className={common.selectPage}>
        <Header message={username} previousStep={LOGIN_STEP.SHOW_USERS} goToStep={goToStep} />
        <article
          aria-live="polite"
          className={style.assistiveStudentPinAnnouncements}
          id="assistiveStudentPinAnnouncements"
          role="region"
        ></article>
        <div
          className={[common.content, common[`content${contentScaling}`]].join(' ')}
          style={{ paddingBottom: '0px !important' }}
        >
          <h2 className={[common.heading, common[`heading${contentScaling}`]].join(' ')}>
            {`${username}, ${t('EnterYourSecretCode')} `}
          </h2>
          <div className={style.container} key={errorId}>
            <div className={style.boxesRow}>{boxes}</div>
            {!error && !loading && !success && <div style={{ height: '5px' }}></div>}
            {loading && <div className="loader"></div>}
            {error && !loading && !success && (
              <p aria-live="assertive" className={style.error} role="alert">
                {t(error.message)}
              </p>
            )}
            {success && !error && !loading && (
              <p aria-live="assertive" className={style.success} role="alert">
                {t(success.message)}
              </p>
            )}
            <div className={style.clearPassword}>
              <ClearPinBtn contentScaling={contentScaling} onClick={this.onPinCleanUp} />
            </div>
            <ul
              className={[style.numbersRow, style[`numbersRow${contentScaling}`]].join(' ')}
              id="letter-list"
            >
              {lettersList}
            </ul>
            <ul className={[style.lettersRow, style[`lettersRow${contentScaling}`]].join(' ')}>
              {numbersList}
            </ul>
          </div>
        </div>
      </div>
    );
  }
}

StudentPinTiles.propTypes = {
  users: PropTypes.array,
  selectedClassId: PropTypes.number,
  selectedUserId: PropTypes.number,
  error: PropTypes.object,
  succcess: PropTypes.object,
  loading: PropTypes.bool,
  loginUser: PropTypes.func,
  goToStep: PropTypes.func,
  contentScaling: PropTypes.number
};

export default StudentPinTiles;
